import React from "react";
import {
  CheckBox,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  FileDownloader,
  StaticDropdown,
} from "src/Components";
import type {
  IPortfoLimitDeviationSummarySearchParams,
  IPortfoLimitDeviationSummaryTableReport,
} from "src/Interfaces/portfolioGroups";
import type { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import {
  getPortfoLimitDeviationSummaryExcel,
  getPortfoLimitDeviationSummaryTable,
  getPortfoLimitDeviationAssetTypesDropdown,
} from "src/Requests/portfolioGroups";
import TableTemplate, { ITableUI } from "src/Tables/TableTemplate/tableTemplate";
import ImbalanceSummaryTableUI from "../ImbalanceSummary/ImbalanceSummaryTableUI";
import PortfolioFilterMultiSelects from "../PortfolioFilterMultiSelects";
import { getPortfolioGroupInstrumentOptions } from "../portfolioGroupInstrumentOptions";
import { portfolioGroupExchangeTypeOptions } from "src/Utils/exchangeTypeOptions";

const columnCategoryOptions: DropdownItem[] = [
  { id: 1, name: "طبقات دارایی / استراتژی" },
  { id: 2, name: "نمادهای استراتژی" },
  { id: 3, name: "نمادهای طبقات دارایی به‌جز آپشن" },
  { id: 4, name: "نمادهای آپشن‌های Naked" },
];

const LimitDeviationSummary = () => {
  const [showHiddenCols, setShowHiddenCols] = React.useState(false);
  const [searchParams, setSearchParams] =
    React.useState<IPortfoLimitDeviationSummarySearchParams>({
      portfoGroupIds: [],
      accountIds: [],
      instrumentId: null,
      exchangeType: null,
      columnCategory: null,
      assetTypes: [],
      onlyUsersWithDeviation: true,
    });

  const filterChangeHandler = (name: string, value: number | string) => {
    setSearchParams((previous) => ({
      ...previous,
      [name]:
        value === "" || value == null
          ? null
          : name === "instrumentId"
            ? String(value)
            : Number(value),
    }));
  };

  const multiSelectFilterChangeHandler = (
    name: "portfoGroupIds" | "accountIds",
    value: string[],
  ) => {
    setSearchParams((previous) => ({
      ...previous,
      [name]: value.map(Number),
    }));
  };

  const assetTypeFilterChangeHandler = (_name: string, value: string[]) => {
    setSearchParams((previous) => ({
      ...previous,
      assetTypes: value.map(Number),
    }));
  };

  const SummaryTableUI = React.useMemo(
    () =>
      ({
        reports,
        onAction,
        sortingType,
        setSortingType,
      }: ITableUI<IPortfoLimitDeviationSummaryTableReport>) => (
        <ImbalanceSummaryTableUI
          reports={reports}
          onAction={onAction}
          sortingType={sortingType}
          setSortingType={setSortingType}
          showHiddenCols={showHiddenCols}
          tableBoxClassName="h-[calc(75vh-10.5rem)] min-h-[360px] portfolio-group-first-row"
        />
      ),
    [showHiddenCols],
  );

  return (
    <div className="flex flex-col flex-1 w-full gap-3">
      <div className="flex flex-row justify-between items-end gap-3">
        <div className="flex flex-row justify-start items-end gap-3">
          <PortfolioFilterMultiSelects
            portfoGroupIds={searchParams.portfoGroupIds ?? []}
            accountIds={searchParams.accountIds ?? []}
            onChange={multiSelectFilterChangeHandler}
          />
          <div className="w-[220px]">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              options={portfolioGroupExchangeTypeOptions}
              onChange={filterChangeHandler}
              value={searchParams.exchangeType?.toString() ?? ""}
            />
          </div>
          <div className="w-[220px]">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "assetTypes",
                placeholder: "طبقه دارایی / استراتژی",
                extraLabel: true,
              }}
              getData={getPortfoLimitDeviationAssetTypesDropdown}
              onChange={assetTypeFilterChangeHandler}
              value={searchParams.assetTypes ?? []}
              withSelectAll
            />
          </div>
          <div className="w-[220px]">
            <DynamicDropdown
              dropdown={{
                name: "instrumentId",
                placeholder: "نماد",
                extraLabel: true,
              }}
              getData={getPortfolioGroupInstrumentOptions}
              onChange={filterChangeHandler}
              value={searchParams.instrumentId ?? ""}
            />
          </div>
          <div className="w-[240px]">
            <StaticDropdown
              dropdown={{
                name: "columnCategory",
                placeholder: "دسته‌بندی ستون‌ها",
                extraLabel: true,
              }}
              options={columnCategoryOptions}
              onChange={filterChangeHandler}
              value={searchParams.columnCategory?.toString() ?? ""}
            />
          </div>
          <div className="w-36">
            <FileDownloader getFile={() => getPortfoLimitDeviationSummaryExcel(searchParams)} />
          </div>
        </div>
      </div>
      <div className="min-h-8 flex items-center gap-6 mb-2">
        <CheckBox
          checked={searchParams.onlyUsersWithDeviation ?? true}
          label="اکانت‌های با مازاد/کسری"
          name="onlyUsersWithDeviation"
          onChange={(_, checked) =>
            setSearchParams((previous) => ({
              ...previous,
              onlyUsersWithDeviation: checked,
            }))
          }
        />
        <CheckBox
          checked={showHiddenCols}
          label="نمایش جزئیات"
          name="showHiddenCols"
          onChange={(_, checked) => setShowHiddenCols(checked)}
        />
      </div>
      <TableTemplate<
        IPortfoLimitDeviationSummarySearchParams,
        IPortfoLimitDeviationSummaryTableReport
      >
        getDataTable={getPortfoLimitDeviationSummaryTable}
        searchParameters={searchParams}
        TableUI={SummaryTableUI}
        refreshTableTime={30000}
        pagesize={200}
        withSort
        tableWrapperClassname="!mx-0 flex justify-center"
      />
    </div>
  );
};

export default LimitDeviationSummary;
